import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  LogOut,
  RefreshCw,
  Search,
  Phone,
  Mail,
  Calendar,
  User,
  BookOpen,
  Filter,
  Trash2
} from 'lucide-react';

import { toast } from 'sonner';

const API_URL = 'https://lighthouse-backend-kh53.onrender.com';

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
 const [stats, setStats] = useState({
  total: 0,
  pending: 0,
  contacted: 0,
  converted: 0,
   NT: 0,
});

const [showManualForm, setShowManualForm] = useState(false);

const [manualInquiry, setManualInquiry] = useState({
  name: '',
  phone: '',
 optionalemail: '',
  grade: '',
  subject: '',
  lead_source: 'Website',
  message: '',
  status: 'pending',
});
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchInquiries();
    fetchStats();
  }, []);

  const fetchInquiries = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch inquiries');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries/${inquiryId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success('Status updated successfully');
        fetchInquiries();
        fetchStats();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update status');
    }
  };

   const deleteInquiry = async (id) => {
  if (!window.confirm("Delete this inquiry?")) return;

  try {
    const response = await fetch(
      `${API_URL}/api/admin/inquiries/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      toast.success("Inquiry deleted successfully");
      fetchInquiries();
      fetchStats();
    } else {
      toast.error("Failed to delete inquiry");
    }
  } catch (error) {
    console.error(error);
    toast.error("Server error");
  }
  };
const addManualInquiry = async () => {
  try {
    const response = await fetch(
      `${API_URL}/api/inquiries`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(manualInquiry),
      }
    );

    if (response.ok) {
      toast.success("Inquiry added successfully");

      setShowManualForm(false);

      setManualInquiry({
        name: "",
        phone: "",
        optionalemail: "",
        grade: "",
        subject: "",
        lead_source: "Website",
        message: "",
        status: "pending",
      });

      fetchInquiries();
      fetchStats();
    } else {
      const error = await response.text();
      console.log(error);
      toast.error("Failed to add inquiry");
    }
  } catch (error) {
    console.error(error);
    toast.error("Server error");
  }
};
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const uniqueSubjects = useMemo(() => {
    const subjects = inquiries
      .map((item) => item.subject)
      .filter((item) => item && item.trim() !== '');
    return [...new Set(subjects)];
  }, [inquiries]);

  const uniqueGrades = useMemo(() => {
    const grades = inquiries
      .map((item) => item.grade)
      .filter((item) => item && item.trim() !== '');
    return [...new Set(grades)];
  }, [inquiries]);

  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inquiry) => {
      const searchLower = searchQuery.toLowerCase();

      const matchesSearch =
        inquiry.name?.toLowerCase().includes(searchLower) ||
        inquiry.email?.toLowerCase().includes(searchLower) ||
        inquiry.phone?.includes(searchQuery) ||
        inquiry.grade?.toLowerCase().includes(searchLower) ||
        inquiry.subject?.toLowerCase().includes(searchLower) ||
        inquiry.message?.toLowerCase().includes(searchLower);

      const matchesStatus =
        statusFilter === 'all' || inquiry.status === statusFilter;

      const matchesSubject =
        subjectFilter === 'all' || inquiry.subject === subjectFilter;

      const matchesGrade =
        gradeFilter === 'all' || inquiry.grade === gradeFilter;

      return matchesSearch && matchesStatus && matchesSubject && matchesGrade;
    });
  }, [inquiries, searchQuery, statusFilter, subjectFilter, gradeFilter]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-700';
      case 'contacted':
        return 'bg-blue-100 text-blue-700';
      case 'converted':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pt-20 pb-12">
      <div className="container mx-auto px-4">
        {/* Header */}
<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">

  <div>
    <h1>Admin Dashboard</h1>
  </div>

 <div className="flex gap-3 flex-wrap">

  <button
    onClick={() => {
      fetchInquiries();
      fetchStats();
    }}
    className="btn-secondary flex items-center gap-2"
  >
    <RefreshCw size={18} />
    Refresh
  </button>

  <button
    onClick={() => setShowManualForm(true)}
    className="bg-green-600 text-white px-4 py-2 rounded-lg"
  >
    + Add Inquiry
  </button>

  <button
    onClick={handleLogout}
    className="btn-primary flex items-center gap-2"
  >
    <LogOut size={18} />
    Logout
  </button>

</div>

</div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm text-gray-500">Total Inquiries</p>
            <h2 className="text-3xl font-bold mt-2">{stats.total}</h2>
          </div>

          <div className="bg-orange-50 rounded-2xl shadow-sm border border-orange-100 p-5">
            <p className="text-sm text-orange-700">Pending</p>
            <h2 className="text-3xl font-bold mt-2">{stats.pending}</h2>
          </div>

          <div className="bg-blue-50 rounded-2xl shadow-sm border border-blue-100 p-5">
            <p className="text-sm text-blue-700">Contacted</p>
            <h2 className="text-3xl font-bold mt-2">{stats.contacted}</h2>
          </div>

          <div className="bg-blue-50 rounded-2xl shadow-sm border border-blue-100 p-5">
            <p className="text-sm text-red-700">NT</p>
            <h2 className="text-3xl font-bold mt-2">{stats.contacted}</h2>
          </div>

          <div className="bg-green-50 rounded-2xl shadow-sm border border-green-100 p-5">
            <p className="text-sm text-green-700">Converted</p>
            <h2 className="text-3xl font-bold mt-2">{stats.converted}</h2>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} />
            <h2 className="font-semibold text-lg">Search & Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search name, phone, email, subject..."
                className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <select
              className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
               <option value="NT">NT</option>
            </select>

            <select
              className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              <option value="all">All Subjects</option>
              {uniqueSubjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>

            <select
              className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white"
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
            >
              <option value="all">All Classes</option>
              {uniqueGrades.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="p-10 text-center text-gray-500">Loading inquiries...</div>
          ) : filteredInquiries.length === 0 ? (
            <div className="p-10 text-center text-gray-500">No inquiries found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[1200px] w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Student</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Class</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Subject</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Phone</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Email</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Message / Details</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Date</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Status</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold">Update</th>
                    
                  </tr>
                </thead>

                <tbody>
                  {filteredInquiries.map((inquiry) => (
                    <tr key={inquiry.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                            <User size={18} className="text-gray-700" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{inquiry.name || '-'}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <span className="text-gray-800">{inquiry.grade || '-'}</span>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center gap-2 text-gray-800">
                          <BookOpen size={15} className="text-gray-500" />
                          <span>{inquiry.subject || '-'}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <a
                          href={`tel:${inquiry.phone}`}
                          className="text-gray-800 hover:underline flex items-center gap-2"
                        >
                          <Phone size={14} className="text-gray-500" />
                          {inquiry.phone || '-'}
                        </a>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <a
                          href={`mailto:${inquiry.email}`}
                          className="text-gray-800 hover:underline flex items-center gap-2 break-all"
                        >
                          <Mail size={14} className="text-gray-500" />
                          {inquiry.email || '-'}
                        </a>
                      </td>

                      <td className="px-4 py-4 align-top max-w-[280px]">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                          {inquiry.message || 'No additional details'}
                        </p>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Calendar size={14} className="text-gray-500" />
                          <span>{formatDate(inquiry.created_at)}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4 align-top">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(inquiry.status)}`}>
                          {inquiry.status || 'pending'}
                        </span>
                      </td>

<td className="px-4 py-4 align-top">
  <div className="flex items-center gap-3">
    <select
      value={inquiry.status}
      onChange={(e) => updateInquiryStatus(inquiry.id, e.target.value)}
      className="px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
    >
      <option value="pending">Pending</option>
      <option value="contacted">Contacted</option>
      <option value="converted">Converted</option>
      <option value="NT">NT</option>
    </select>

    <button
      onClick={() => deleteInquiry(inquiry.id)}
      className="text-red-600 hover:text-red-800"
      title="Delete Inquiry"
    >
      <Trash2 size={18} />
    </button>
  </div>
</td>
                      
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      {showManualForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <h2 className="text-xl font-bold mb-4">
  Add Manual Inquiry
</h2>

<input
  type="text"
  placeholder="Student Name"
  value={manualInquiry.name}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      name: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
/>

<input
  type="text"
  placeholder="Phone Number"
  value={manualInquiry.phone}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      phone: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
/>

<input
  type="email"
  placeholder="Email (Optional)"
  value={manualInquiry.email}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      email: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
/>

<input
  type="text"
  placeholder="Class"
  value={manualInquiry.grade}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      grade: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
/>

<input
  type="text"
  placeholder="Subject"
  value={manualInquiry.subject}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      subject: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
/>

<select
  value={manualInquiry.lead_source}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      lead_source: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
>
  <option value="Justdial">Justdial</option>
   <option value="TutorWave">Website</option>
  <option value="Walk-in">Walk-in</option>
  <option value="Reference">Reference</option>
  <option value="Instagram">Instagram</option>
</select>

<textarea
  placeholder="Remarks / Message"
  value={manualInquiry.message}
  onChange={(e) =>
    setManualInquiry({
      ...manualInquiry,
      message: e.target.value,
    })
  }
  className="w-full border p-2 rounded mb-3"
/>

<div className="flex gap-3">
  <button
  onClick={addManualInquiry}
  className="bg-green-600 text-white px-4 py-2 rounded-lg"
>

    Save Inquiry
  </button>

  <button
    onClick={() => setShowManualForm(false)}
    className="bg-gray-500 text-white px-4 py-2 rounded-lg"
  >
    Close
  </button>
</div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
